// app/src/api/hospitalApi.ts

import { serverApi, handleApiResponse, getAuthHeaders, queApi } from "@core/api/core"
import { createAsyncThunk } from "@reduxjs/toolkit"
import { HospitalResponse, HospitalSerializer, initialHospitalState, HospitalBuhangcupSerializer, HospitalBuhangcupResponse } from "@/types/hospital"
import { CommonTResponse } from "@/types/common"

/**
 * Redux Toolkit의 createAsyncThunk를 사용한 접수 생성 thunk.
 * @param params - { patientUuid, insuranceOrder } 정보를 포함하는 객체
 * @returns Promise<number>, 성공 시 생성된 접수번호를 반환, 실패 시 에러 메시지를 reject
 */

/**
 * 서버 PC 이름 조회 API
 */
export const getServerPcName = async (): Promise<string> => {
  const requestUrl = `/hospital/public/info/server-info/`
  const response = await queApi.get(requestUrl)
  const result = handleApiResponse<{ pcName: string }>(response)
  return result.pcName
}

// 로컬 캐시를 이용하여 공개 병원 정보를 조회하는 비동기 함수
export const fetchPublicHospitalInfo = createAsyncThunk<HospitalSerializer, { refreshRemote?: boolean } | void, { rejectValue: string }>(
  "hospital/fetchPublicHospitalInfo",
  async (options, { rejectWithValue }) => {
    const cachedHospitalInfoKey = "cached_public_hospital_info"

    try {
      const requestUrl = `/hospital/public/info/`
      const params = options?.refreshRemote ? { refresh_remote: true } : undefined
      const response = await queApi.get<CommonTResponse<HospitalSerializer[]>>(requestUrl, { params })
      const hospitalInfos = handleApiResponse<HospitalSerializer[]>(response)
      const firstHospitalInfo = hospitalInfos[0] // 첫 번째 병원 데이터 사용

      if (firstHospitalInfo) {
        // 캐시 저장
        localStorage.setItem(cachedHospitalInfoKey, JSON.stringify(firstHospitalInfo))
        return firstHospitalInfo
      } else {
        console.error("의료기관 정보가 없습니다.")
        return rejectWithValue("의료기관 정보가 없습니다.")
      }
    } catch (err: any) {
      // 토큰이 없거나 네트워크 에러 시 로컬 캐시 fallback
      const cached = localStorage.getItem(cachedHospitalInfoKey)
      if (cached) {
        const cachedHospitalInfo = JSON.parse(cached) as HospitalSerializer
        return {
          ...cachedHospitalInfo,
          hospitalContractReceived: null,
          hospitalCmsFeeReceived: null,
          hospitalCmsDigitalSignatureReceived: null,
          hospitalCmsSmsReceived: null,
        }
      }
      return rejectWithValue(err.message || "의료기관 조회 오류입니다.")
    }
  }
)

export const getHospitalList = async (): Promise<HospitalSerializer[]> => {
  const requestUrl = `/hospital/admin/`
  const response = await serverApi.get<CommonTResponse<HospitalSerializer[]>>(requestUrl)
  return handleApiResponse<HospitalSerializer[]>(response)
}

export const createHospitalInfo = async (hospitalInfo: HospitalSerializer): Promise<HospitalSerializer> => {
  const requestUrl = `/hospital/admin/`
  const response = await serverApi.post<HospitalResponse>(requestUrl, hospitalInfo)
  return handleApiResponse<HospitalSerializer>(response)
}

export const updateHospitalInfo = async (hospitalInfo: HospitalSerializer): Promise<HospitalSerializer> => {
  const requestUrl = `/hospital/admin/${hospitalInfo.hospitalUuid}/`
  const response = await serverApi.patch<HospitalResponse>(requestUrl, hospitalInfo)
  return handleApiResponse<HospitalSerializer>(response)
}

///////////////////////////////////////////////////////////////
// 부항컵 설정
///////////////////////////////////////////////////////////////

export const getHospitalBuhangcup = async (): Promise<HospitalBuhangcupSerializer[]> => {
  const requestUrl = `/hospital/hospitalbuhangcup/`
  const response = await serverApi.get<HospitalBuhangcupResponse>(requestUrl)

  if (response.status === 204) {
    return initialHospitalState as HospitalBuhangcupSerializer[]
  }

  return handleApiResponse<HospitalBuhangcupSerializer[]>(response)
}

export const createHospitalBuhangcup = async (buhangcupId: number): Promise<HospitalBuhangcupSerializer[]> => {
  const requestUrl = `/hospital/hospitalbuhangcup/`
  const response = await serverApi.post<HospitalBuhangcupResponse>(requestUrl, { buhangcupId })
  return handleApiResponse<HospitalBuhangcupSerializer[]>(response)
}

export const updateHospitalBuhangcup = async (hospitalBuhangcupData: HospitalBuhangcupSerializer): Promise<HospitalBuhangcupSerializer[]> => {
  const requestUrl = `/hospital/hospitalbuhangcup/${hospitalBuhangcupData.hospitalBuhangcupId}/`
  const response = await serverApi.patch<HospitalBuhangcupResponse>(requestUrl, hospitalBuhangcupData)
  return handleApiResponse<HospitalBuhangcupSerializer[]>(response)
}

export const deleteHospitalBuhangcup = async (hospitalBuhangcupId: number): Promise<HospitalBuhangcupSerializer[]> => {
  const requestUrl = `/hospital/hospitalbuhangcup/${hospitalBuhangcupId}/`
  const response = await serverApi.delete<HospitalBuhangcupResponse>(requestUrl)
  return handleApiResponse<HospitalBuhangcupSerializer[]>(response)
}

export const fetchPublicBuhangcup = createAsyncThunk<
  HospitalBuhangcupSerializer | null, // 성공 시 반환할 타입
  void, // 인자로 받을 객체 타입
  { rejectValue: string }
>("hospital/fetchPublicBuhangcup", async (_, { rejectWithValue }) => {
  try {
    const requestUrl = `/hospital/public/hospitalbuhangcup/`
    const response = await queApi.get<HospitalBuhangcupResponse>(requestUrl)
    const resultList = handleApiResponse<HospitalBuhangcupSerializer[]>(response)
    return resultList[0] ?? null
  } catch (err: any) {
    return rejectWithValue(err.message || "부항컵 조회 오류입니다.")
  }
})

